// AdminGlobalRas — Owner-only CRUD für globale RAS-Regeln (eve_ras_global).

import { useEffect, useState } from 'react';
import { Check, Plus, Trash2, X } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';

interface GlobalRasRow {
  id: string;
  rule: string;
  trigger_context: string | null;
  is_active: number;
  created_at: string | null;
  rule_key: string | null;
  category: string | null;
  priority: number | null;
}

export function AdminGlobalRas() {
  const accessToken = useAuthStore(selectAccessToken);
  const [rows, setRows] = useState<GlobalRasRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    rule: '',
    trigger_context: '',
    category: 'general',
    priority: 100,
  });

  async function reload() {
    if (!accessToken) return;
    setLoading(true);
    try {
      const data = await apiFetch<GlobalRasRow[]>('/admin/global-ras', { accessToken });
      setRows(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  async function handleAdd() {
    if (!accessToken || draft.rule.trim().length === 0) return;
    try {
      await apiFetch('/admin/global-ras', {
        method: 'POST',
        accessToken,
        json: {
          rule: draft.rule.trim(),
          trigger_context: draft.trigger_context.trim() || null,
          category: draft.category,
          priority: draft.priority,
          is_active: true,
        },
      });
      setDraft({ rule: '', trigger_context: '', category: 'general', priority: 100 });
      setAdding(false);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    }
  }

  async function handleToggle(row: GlobalRasRow) {
    if (!accessToken) return;
    try {
      await apiFetch(`/admin/global-ras/${row.id}`, {
        method: 'PATCH',
        accessToken,
        json: { is_active: row.is_active === 0 },
      });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update fehlgeschlagen');
    }
  }

  async function handleDelete(row: GlobalRasRow) {
    if (!accessToken) return;
    if (!window.confirm(`Regel „${row.rule.slice(0, 60)}…" wirklich löschen?`)) return;
    try {
      await apiFetch(`/admin/global-ras/${row.id}`, { method: 'DELETE', accessToken });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
    }
  }

  return (
    <div className="glass-strong rounded-2xl p-5">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold">Globale RAS-Regeln</h2>
          <div className="mt-1 text-[11px] text-neutral-500">
            {rows.length.toLocaleString('en-US')} Regeln · {rows.filter((r) => r.is_active === 1).length} aktiv
          </div>
        </div>
        <button
          type="button"
          onClick={() => setAdding((s) => !s)}
          className="inline-flex items-center gap-1 rounded-lg border border-amber-500/30 bg-amber-500/15 px-3 py-1.5 text-[12px] font-bold text-amber-300 hover:bg-amber-500/25"
        >
          <Plus className="size-3" />
          Neue Regel
        </button>
      </div>

      {error ? (
        <div className="mb-3 rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
          {error}
        </div>
      ) : null}

      {adding ? (
        <div className="mb-4 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
          <div className="grid gap-2 md:grid-cols-2">
            <textarea
              value={draft.rule}
              onChange={(e) => setDraft({ ...draft, rule: e.target.value })}
              placeholder='Rule-Text (z.B. „Niemals halluzinieren bei Trade-Calls")'
              rows={3}
              className="rounded border border-white/10 bg-neutral-900/60 px-2 py-1.5 text-[12px] outline-none focus:border-amber-500/40 md:col-span-2"
            />
            <input
              type="text"
              value={draft.trigger_context}
              onChange={(e) => setDraft({ ...draft, trigger_context: e.target.value })}
              placeholder="Trigger-Kontext (optional)"
              className="rounded border border-white/10 bg-neutral-900/60 px-2 py-1.5 text-[12px] outline-none focus:border-amber-500/40"
            />
            <div className="flex gap-2">
              <select
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                className="flex-1 rounded border border-white/10 bg-neutral-900/60 px-2 py-1.5 text-[12px]"
              >
                <option value="general">general</option>
                <option value="trading">trading</option>
                <option value="persona">persona</option>
                <option value="safety">safety</option>
              </select>
              <input
                type="number"
                value={draft.priority}
                onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) || 100 })}
                placeholder="Priority"
                className="w-24 rounded border border-white/10 bg-neutral-900/60 px-2 py-1.5 text-[12px]"
              />
            </div>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="rounded-lg border border-white/10 px-3 py-1 text-[12px] text-neutral-300 hover:bg-white/5"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => void handleAdd()}
              disabled={draft.rule.trim().length === 0}
              className="rounded-lg bg-amber-500 px-3 py-1 text-[12px] font-bold text-neutral-950 hover:bg-amber-400 disabled:bg-neutral-700 disabled:text-neutral-500"
            >
              Speichern
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="py-8 text-center text-[12px] text-neutral-500">Lade Regeln…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-[12px] text-neutral-500">
          Noch keine globalen RAS-Regeln definiert.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li
              key={r.id}
              className={`flex items-start gap-2 rounded-lg border p-2.5 ${
                r.is_active === 1 ? 'border-white/5 bg-white/[0.02]' : 'border-white/5 bg-white/[0.01] opacity-60'
              }`}
            >
              <button
                type="button"
                onClick={() => void handleToggle(r)}
                aria-label={r.is_active === 1 ? 'Regel deaktivieren' : 'Regel aktivieren'}
                className={
                  r.is_active === 1
                    ? 'grid size-5 shrink-0 place-items-center rounded border border-emerald-500/30 bg-emerald-500/15 text-emerald-300'
                    : 'grid size-5 shrink-0 place-items-center rounded border border-white/10 text-neutral-500 hover:text-neutral-200'
                }
              >
                {r.is_active === 1 ? <Check className="size-3" /> : <X className="size-3" />}
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-neutral-200">{r.rule}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[10px] text-neutral-500">
                  {r.category ? (
                    <span className="rounded bg-neutral-800/60 px-1.5 py-0.5">{r.category}</span>
                  ) : null}
                  <span>prio {r.priority ?? 100}</span>
                  {r.trigger_context ? (
                    <>
                      <span>·</span>
                      <span className="italic">when: {r.trigger_context}</span>
                    </>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void handleDelete(r)}
                aria-label="Regel löschen"
                className="text-neutral-500 hover:text-rose-400"
              >
                <Trash2 className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
